import { create } from 'zustand';
import type { MinecraftOverview, MinecraftServer } from '@spawnwire/shared';
import { http } from '../../lib/api';

const CONSOLE_LINES = 500;

interface MinecraftState {
  overview: MinecraftOverview | null;
  error: string | null;
  console: Record<string, string[]>;
}

export const useMinecraft = create<MinecraftState>(() => ({ overview: null, error: null, console: {} }));

export const minecraft = {
  async load(): Promise<void> {
    try {
      const overview = await http.get<MinecraftOverview>('/minecraft');
      useMinecraft.setState({ overview, error: null });
    } catch (err) {
      useMinecraft.setState({ error: err instanceof Error ? err.message : 'Could not load your servers.' });
    }
  },
  upsert(server: MinecraftServer): void {
    const { overview } = useMinecraft.getState();
    if (!overview) return;
    const exists = overview.servers.some((s) => s.id === server.id);
    useMinecraft.setState({
      overview: { ...overview, servers: exists ? overview.servers.map((s) => (s.id === server.id ? server : s)) : [...overview.servers, server] },
    });
  },
  remove(serverId: string): void {
    const { overview, console } = useMinecraft.getState();
    const { [serverId]: _gone, ...rest } = console;
    useMinecraft.setState({ console: rest, overview: overview && { ...overview, servers: overview.servers.filter((s) => s.id !== serverId) } });
  },
  setConsole(serverId: string, lines: string[]): void {
    useMinecraft.setState((s) => ({ console: { ...s.console, [serverId]: lines.slice(-CONSOLE_LINES) } }));
  },
  appendConsole(serverId: string, lines: string[]): void {
    useMinecraft.setState((s) => ({ console: { ...s.console, [serverId]: [...(s.console[serverId] ?? []), ...lines].slice(-CONSOLE_LINES) } }));
  },
};
